// Foyer glue, not a port - the CMS-preview-only diagnostics overlay for
// SignageEngineSurface, analogous to (not a copy of) SignageSurface.tsx's
// own `Diagnostics` component.
//
// DELIBERATELY NOT a faithful port of that component: `Diagnostics` is
// deeply coupled to SignageSurface.tsx's own internal shapes (`Head`,
// `PollStatus`, `explainEmpty`, `heldIndex`/`effectiveIndex`, ...), none of
// which exist in the ported engine's world. This is a fresh, smaller
// implementation built from what SignageEngineSurface actually has -
// real state-machine snapshots, not wall-clock estimation - covering the
// same JOB (answer "why is nothing on screen, or is everything fine" for
// an operator previewing through the CMS, where a caption costs nothing)
// with an honest, narrower fact set. Same visual language (a bottom-anchored
// dark gradient bar, `data-testid="signage-diagnostics"` matching the
// existing renderer's own testid so a future CMS preview harness can query
// either the same way) so the two surfaces read as the same product to
// whoever is looking, not two unrelated debug tools.
import type { SignageTimeline } from "@shared/types/signage";
import type { SignagePlaybackReport } from "@shared/types/foyer";

export type PollStatus =
  | { state: "loading" }
  | { state: "ok"; at: number }
  | { state: "http"; code: number; at: number }
  | { state: "offline"; at: number };

interface SignageDiagnosticsProps {
  screenId: string;
  timeline: SignageTimeline | null;
  pollStatus: PollStatus;
  report: SignagePlaybackReport;
  clockSkewSeconds: number;
}

/** HH:mm in the viewer's own zone - read by the person at the laptop, not
 *  by the wall. Mirrors SignageSurface.tsx's own `clockOf`. */
function clockOf(d: Date): string {
  return `${String(d.getHours()).padStart(2, "0")}:${String(
    d.getMinutes()
  ).padStart(2, "0")}`;
}

function headlineFor(report: SignagePlaybackReport): {
  headline: string;
  detail: string;
} {
  if (report.state === "playing") return { headline: "", detail: "" };
  if (report.state === "blocked") {
    return {
      headline: "Blocked",
      detail:
        report.idleReason ??
        "The engine has a schedule but cannot play it right now - see the media/poll facts below.",
    };
  }
  return {
    headline: "Nothing scheduled right now",
    detail:
      report.idleReason ??
      "The resolver has nothing for this screen at this instant, or the schedule has not arrived yet.",
  };
}

export default function SignageDiagnostics({
  screenId,
  timeline,
  pollStatus,
  report,
  clockSkewSeconds,
}: SignageDiagnosticsProps) {
  const facts: string[] = [`screen ${screenId}`];
  if (report.breakActive) facts.push("break active");
  if (report.playlistName) facts.push(report.playlistName);
  if (report.itemIndex && report.itemCount) {
    facts.push(`item ${report.itemIndex}/${report.itemCount}`);
  }
  if (report.failedItems) facts.push(`${report.failedItems} failed`);
  if (report.recoveries) facts.push(`recovered ${report.recoveries}x`);
  if (report.keySystem) facts.push(report.keySystem);
  // Same threshold as SignageSurface.tsx's own Diagnostics: under a minute
  // is the normal wobble of a panel corrected every 30s, and printing it
  // always would train people to ignore the line.
  if (Math.abs(clockSkewSeconds) >= 60) {
    facts.push(
      `clock ${clockSkewSeconds > 0 ? "-" : "+"}${Math.abs(
        Math.round(clockSkewSeconds / 60)
      )}min (corrected)`
    );
  }
  if (timeline?.revision) facts.push(`rev ${timeline.revision}`);
  facts.push(
    pollStatus.state === "ok"
      ? `polled ${clockOf(new Date(pollStatus.at))}`
      : pollStatus.state === "offline"
        ? "offline"
        : pollStatus.state === "http"
          ? `HTTP ${pollStatus.code}`
          : "polling…"
  );

  const empty = report.state !== "playing";
  const { headline, detail } = headlineFor(report);

  return (
    <div
      data-testid="signage-diagnostics"
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        padding: empty ? "32px 40px" : "12px 40px",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        background: empty
          ? "transparent"
          : "linear-gradient(to top, rgba(0,0,0,.72), rgba(0,0,0,0))",
        color: "#fff",
        fontFamily:
          'ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif',
        pointerEvents: "none",
      }}
    >
      {empty && (
        <>
          <div style={{ fontSize: 34, fontWeight: 600, letterSpacing: -0.5 }}>
            {headline}
          </div>
          {detail && (
            <div
              style={{
                fontSize: 18,
                lineHeight: 1.45,
                maxWidth: "62ch",
                color: "rgba(255,255,255,.72)",
              }}
            >
              {detail}
            </div>
          )}
        </>
      )}
      <div
        style={{
          fontSize: 13,
          lineHeight: 1.4,
          color: "rgba(255,255,255,.65)",
          fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
        }}
      >
        {facts.join(" · ")}
      </div>
    </div>
  );
}
